// A minimal listening indicator with a Stop button, for any of the operator's
// pages that stay open during a call held elsewhere (the prep page above all).
// Adam, 2026-10-05: "a minimal listening indicator, and i'll have the prep page
// pulled up while on the teams call so i want an indicator and stop button on
// that page as well." It reads the same signal the Listen tab reports
// (worker/src/line.ts `listenBeat`) and shows nothing when nobody is listening,
// except for a moment after a session it was showing ends, to say the
// recording was saved.

import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { LISTEN_BEAT_MS, type ListenSaved, type ListenStatus } from "@breeze/core";
import { api } from "../lib/api";
import { getUserKey } from "../lib/storage";
import { SAVED_HOLD_MS, SavedRow } from "./ListenInline";

export function useListeningStatus(): {
  readonly status: ListenStatus | null;
  /** Set for a moment after a session this page was showing ends. */
  readonly ended: ListenSaved | null;
  readonly stop: () => void;
  readonly pause: (paused: boolean) => void;
} {
  const [status, setStatus] = useState<ListenStatus | null>(null);
  const [ended, setEnded] = useState<ListenSaved | null>(null);
  const showing = useRef(false);

  useEffect(() => {
    if (!getUserKey()) return;
    let alive = true;
    let hide: ReturnType<typeof setTimeout> | undefined;
    const read = (): void => {
      if (document.visibilityState === "hidden") return;
      api<ListenStatus>("/api/listen/status")
        .then((next) => {
          if (!alive) return;
          if (next.listening) {
            showing.current = true;
            setEnded(null);
            setStatus(next);
            return;
          }
          // Only a session this page saw end gets the confirmation, so a page
          // opened just after one ends shows nothing.
          if (showing.current && next.ended) {
            showing.current = false;
            setEnded(next.ended.saved);
            clearTimeout(hide);
            hide = setTimeout(() => setEnded(null), SAVED_HOLD_MS + 300);
          } else if (showing.current) {
            showing.current = false;
          }
          setStatus(null);
        })
        .catch(() => undefined);
    };
    read();
    const timer = setInterval(read, LISTEN_BEAT_MS);
    document.addEventListener("visibilitychange", read);
    return () => {
      alive = false;
      clearInterval(timer);
      clearTimeout(hide);
      document.removeEventListener("visibilitychange", read);
    };
  }, []);

  const stop = useCallback((): void => {
    // Shown as stopping at once; the Listen tab lets go on its next report.
    setStatus((s) => (s ? { ...s, stopping: true } : s));
    api<ListenStatus>("/api/listen/stop", { method: "POST" }).catch(() => undefined);
  }, []);

  const pause = useCallback((paused: boolean): void => {
    // Shown at once; the Listen tab acts on its next report and the next read confirms it.
    setStatus((s) => (s ? { ...s, paused } : s));
    api<ListenStatus>("/api/listen/pause", { method: "POST", body: { paused } }).catch(() => undefined);
  }, []);

  return { status, ended, stop, pause };
}

export function ListeningIndicator(): JSX.Element | null {
  const { status, ended, stop, pause } = useListeningStatus();
  if (ended) {
    return (
      <div className="pg__listen">
        <SavedRow saved={ended} testId="listening-indicator-saved" />
      </div>
    );
  }
  if (!status) return null;
  if (status.saving) {
    return (
      <div className="pg__listen">
        <SavedRow saved="saving" testId="listening-indicator-saving" />
      </div>
    );
  }
  const heard = status.theirs === "on";
  const paused = !!status.paused;
  const word = status.stopping ? "Stopping" : paused ? "Paused" : heard ? "Recording" : "Recording, their side is not shared";
  // The same row as the Listen tab's own (ListenInline), so the bar looks the
  // same whichever page it is on.
  return (
    <div className="pg__listen">
      <div className="linl" data-testid="listening-indicator" data-stopping={status.stopping ? "1" : "0"}>
        <span className="lsi__dot" aria-hidden="true" data-quiet={heard && !paused && !status.stopping ? "0" : "1"} />
        <span className="linl__word" role="status">
          {word}
        </span>
        <button
          type="button"
          className="lnotes__btn"
          onClick={() => pause(!paused)}
          aria-pressed={paused}
          disabled={!!status.stopping}
        >
          {paused ? "Resume" : "Pause"}
        </button>
        <button type="button" className="lnotes__stop" onClick={stop} disabled={!!status.stopping}>
          Stop
        </button>
      </div>
    </div>
  );
}
